import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Loader2, Key, Eye, EyeOff, ShieldAlert } from "lucide-react";
import { api } from "../context/AuthContext";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

export default function ForcePasswordChangeDialog({ open, onSuccess }) {
    const [form, setForm] = useState({ current_password: "", new_password: "", confirm_password: "" });
    const [submitting, setSubmitting] = useState(false);
    const [showCurrent, setShowCurrent] = useState(false);
    const [showNew, setShowNew] = useState(false);

    const { t } = useTranslation();

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!form.current_password || !form.new_password) {
            return toast.error(t("auth.passwords.fillAll"));
        }

        if (form.new_password.length < 8) {
            return toast.error(t("auth.passwordLength"));
        }

        if (form.new_password !== form.confirm_password) {
            return toast.error(t("auth.passwords.matchError"));
        }

        setSubmitting(true);
        try {
            await api.post("/users/me/change-password", {
                current_password: form.current_password,
                new_password: form.new_password
            });
            toast.success(t("auth.passwords.updated"));
            onSuccess();
        } catch (error) {
            toast.error(error.response?.data?.detail || t("errors.serverError"));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open}>
            <DialogContent className="sm:max-w-md" onInteractOutside={(e) => e.preventDefault()}>
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <ShieldAlert className="w-5 h-5 text-amber-500" />
                        {t("auth.passwords.required")}
                    </DialogTitle>
                    <DialogDescription>
                        {t("auth.passwords.requiredDesc")}
                    </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="space-y-2">
                        <Label>{t("auth.passwords.currentTemp")}</Label>
                        <div className="relative">
                            <Input
                                type={showCurrent ? "text" : "password"}
                                value={form.current_password}
                                onChange={(e) => setForm({ ...form, current_password: e.target.value })}
                                placeholder={t("auth.passwords.currentTempPlaceholder")}
                                className="pr-10"
                            />
                            <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="absolute right-0 top-0 h-full px-3"
                                onClick={() => setShowCurrent(!showCurrent)}
                            >
                                {showCurrent ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                            </Button>
                        </div>
                    </div>
                    <div className="space-y-2">
                        <Label>{t("auth.passwords.new")}</Label>
                        <div className="relative">
                            <Input
                                type={showNew ? "text" : "password"}
                                value={form.new_password}
                                onChange={(e) => setForm({ ...form, new_password: e.target.value })}
                                placeholder={t("auth.passwords.newPlaceholder")}
                                className="pr-10"
                            />
                            <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="absolute right-0 top-0 h-full px-3"
                                onClick={() => setShowNew(!showNew)}
                            >
                                {showNew ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                            </Button>
                        </div>
                    </div>
                    <div className="space-y-2">
                        <Label>{t("auth.passwords.confirm")}</Label>
                        <Input
                            type="password"
                            value={form.confirm_password}
                            onChange={(e) => setForm({ ...form, confirm_password: e.target.value })}
                            placeholder={t("auth.passwords.confirmPlaceholder")}
                        />
                    </div>
                    <Button type="submit" className="w-full" disabled={submitting}>
                        {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Key className="w-4 h-4 mr-2" />}
                        {t("auth.passwords.submit")}
                    </Button>
                </form>
            </DialogContent>
        </Dialog>
    );
}
